import type { ReportShipmentRow } from './build-shipments-section.ts';
import type { CommercialPlanSectionData } from './types.ts';

// Arma el plan comercial con las reglas de la pantalla de Programa Comercial:
// la unidad y la fecha base (ETA o ETD) son las del programa, cada embarque
// (una referencia) cae en la semana de su fecha declarada y cuenta todo lo
// cargado, haya zarpado o no. Lo real se separa en arribado, en tránsito y
// por zarpar.

export type ReportProgram = {
  unit: 'kg' | 'ceq' | 'shipment';
  date_basis: 'eta' | 'etd';
  kg_per_ceq: number;
};

// Compromisos vigentes, ya convertidos a la unidad del programa.
export type ReportCommitment = {
  receiver: string;
  week: number;
  quantity: number;
};

// El resto de los clientes, para comparar cumplimientos: solo los que tienen
// compromisos, así los despachos a clientes sin plan (spot) no inflan el %.
export const restOfClients = <Box extends { receiver?: string | null }>({
  commitments,
  boxes,
  receivers,
}: {
  commitments: ReportCommitment[];
  boxes: Box[];
  // Clientes del reporte.
  receivers: Set<string>;
}) => {
  const rest = commitments.filter((c) => !receivers.has(c.receiver));
  const restReceivers = new Set(rest.map((c) => c.receiver));
  return {
    commitments: rest,
    boxes: boxes.filter((box) => restReceivers.has(box.receiver || '')),
    clients: restReceivers.size,
  };
};

const shippedAmount = (row: ReportShipmentRow, program: ReportProgram) => {
  if (program.unit === 'shipment') return 1;
  if (program.unit === 'ceq')
    return row.kilos / (Number(program.kg_per_ceq) || 1);
  return row.kilos;
};

export const buildCommercialPlanSection = ({
  program,
  commitments,
  rows,
  weeks,
  cutoffIndex,
  weekOf,
}: {
  program: ReportProgram | null;
  commitments: ReportCommitment[];
  rows: ReportShipmentRow[];
  weeks: number[];
  cutoffIndex: number;
  weekOf: (date: string) => number | null;
}): CommercialPlanSectionData => {
  const committedByWeek = new Map<number, number>();
  for (const commitment of commitments) {
    committedByWeek.set(
      commitment.week,
      (committedByWeek.get(commitment.week) ?? 0) + (commitment.quantity || 0)
    );
  }
  const committed = Array.from(committedByWeek.values()).reduce(
    (a, b) => a + b,
    0
  );
  if (!program || committed <= 0) return { hasPlan: false };

  type Shipped = { arrived: number; inTransit: number; notDeparted: number };
  const shippedByWeek = new Map<number, Shipped>();
  for (const row of rows) {
    const date = program.date_basis === 'eta' ? row.plannedEta : row.plannedEtd;
    const week = date ? weekOf(date) : null;
    if (week == null) continue;
    const shipped = shippedByWeek.get(week) || { arrived: 0, inTransit: 0, notDeparted: 0 };
    const bucket = !row.departed ? 'notDeparted' : row.arrived ? 'arrived' : 'inTransit';
    shipped[bucket] += shippedAmount(row, program);
    shippedByWeek.set(week, shipped);
  }

  // Lo real cuenta hasta la semana de corte; después solo queda el plan.
  const seasonWeeks = weeks.map((week, index) => {
    const isPast = index <= cutoffIndex;
    const shipped = isPast ? shippedByWeek.get(week) : undefined;
    return {
      week,
      committed: committedByWeek.get(week) ?? 0,
      arrived: shipped?.arrived ?? 0,
      inTransit: shipped?.inTransit ?? 0,
      notDeparted: shipped?.notDeparted ?? 0,
      isPast,
    };
  });

  const sum = (
    list: typeof seasonWeeks,
    pick: (w: (typeof seasonWeeks)[0]) => number
  ) => list.reduce((total, week) => total + pick(week), 0);
  const toDate = seasonWeeks.filter((week) => week.isPast);
  const shipped = (week: (typeof seasonWeeks)[0]) =>
    week.arrived + week.inTransit + week.notDeparted;

  return {
    hasPlan: true,
    unit: program.unit,
    dateBasis: program.date_basis,
    weeks: seasonWeeks,
    totals: {
      committed,
      committedToDate: sum(toDate, (week) => week.committed),
      shippedToDate: sum(toDate, shipped),
      shipped: sum(seasonWeeks, shipped),
    },
  };
};
